import type { QuoteInterval, QuotePaymentMode, QuotePaymentStatus, QuoteStatus } from "@/v2Projects/types";
import { quoteCopy, quoteIntervalLabel } from "@/v2Projects/utils/quoteCopy";
import { formatCurrency } from "@/v2Projects/utils/display";

export const quotePaymentStatusOptions: { value: QuotePaymentStatus; label: string }[] = [
  { value: "unpaid", label: "Unpaid" },
  { value: "partial_paid", label: "Partially Paid" },
  { value: "paid", label: "Paid" },
  { value: "partial_refund", label: "Partial Refund" },
  { value: "refund", label: "Refund" },
  { value: "failed", label: "Failed" },
];

export function normalizeQuotePaymentStatus(
  status: QuotePaymentStatus | null | undefined,
): QuotePaymentStatus {
  return status ?? "unpaid";
}

export function isClientQuoteActionable(quote: {
  status: QuoteStatus | string;
  payment_status?: QuotePaymentStatus | string | null;
}): boolean {
  if (quote.status !== "offered") return false;
  return !quote.payment_status || quote.payment_status === "unpaid" || quote.payment_status === "failed";
}

export function getQuoteCardStatusLabel(quote: {
  status: QuoteStatus | string | null | undefined;
  payment_status?: QuotePaymentStatus | string | null;
}): string {
  switch (quote.status) {
    case "offered":
      switch (quote.payment_status ?? "unpaid") {
        case "failed":
          return "Payment Failed";
        case "partial_paid":
          return "Partially Paid";
        case "paid":
          return "Paid";
        case "partial_refund":
          return "Partial Refund";
        case "refund":
          return "Refunded";
        default:
          return "Pending Review";
      }
    case "accepted":
      switch (quote.payment_status ?? "unpaid") {
        case "partial_paid":
          return "Partially Paid";
        case "paid":
          return "Paid";
        case "partial_refund":
          return "Partial Refund";
        case "refund":
          return "Refunded";
        default:
          return "Accepted";
      }
    case "rejected":
      return "Rejected";
    case "cancelled":
      return "Cancelled";
    default:
      return formatQuoteStatusLabel(String(quote.status ?? "unknown"));
  }
}

export interface QuoteCardStatusBadgeColors {
  bgClass: string;
  textClass: string;
  borderClass: string;
}

export function getQuoteCardStatusBadgeColors(quote: {
  status: QuoteStatus | string | null | undefined;
  payment_status?: QuotePaymentStatus | string | null;
}): QuoteCardStatusBadgeColors {
  if (quote.status === "offered" || quote.status === "accepted") {
    switch (quote.payment_status ?? "unpaid") {
      case "paid":
        return { bgClass: "bg-emerald-50", textClass: "text-emerald-700", borderClass: "border-emerald-200" };
      case "partial_paid":
        return { bgClass: "bg-amber-50", textClass: "text-amber-700", borderClass: "border-amber-200" };
      case "partial_refund":
        return { bgClass: "bg-orange-50", textClass: "text-orange-700", borderClass: "border-orange-200" };
      case "refund":
        return { bgClass: "bg-violet-50", textClass: "text-violet-700", borderClass: "border-violet-200" };
      case "failed":
        return { bgClass: "bg-red-50", textClass: "text-red-700", borderClass: "border-red-200" };
      default:
        if (quote.status === "accepted") {
          return { bgClass: "bg-emerald-50", textClass: "text-emerald-700", borderClass: "border-emerald-200" };
        }
    }
  }

  switch (quote.status) {
    case "offered":
      return { bgClass: "bg-[#D3D1F6]", textClass: "text-[#393759]", borderClass: "border-none" };
    case "rejected":
      return { bgClass: "bg-red-50", textClass: "text-red-700", borderClass: "border-red-200" };
    case "cancelled":
      return { bgClass: "bg-gray-100", textClass: "text-gray-600", borderClass: "border-gray-200" };
    default:
      return { bgClass: "bg-gray-100", textClass: "text-gray-600", borderClass: "border-gray-200" };
  }
}

export function getQuoteCardStatusBadgeClass(quote: {
  status: QuoteStatus | string | null | undefined;
  payment_status?: QuotePaymentStatus | string | null;
}): string {
  const { bgClass, textClass, borderClass } = getQuoteCardStatusBadgeColors(quote);
  return `${bgClass} ${textClass} ${borderClass}`;
}

export function getQuotePaymentUrl(quote: {
  id?: number | null;
  quote_id?: number | null;
  payment_url?: string | null;
  provider_payment_url?: string | null;
  external_payment_url?: string | null;
  payment_mode?: QuotePaymentMode | string | null;
  type?: QuotePaymentMode | string | null;
  payment_provider?: string | null;
  latest_payment_provider?: string | null;
}): string | null {
  const paymentUrl = quote.payment_url ?? quote.provider_payment_url ?? quote.external_payment_url ?? null;
  const quoteId = quote.quote_id ?? quote.id ?? null;
  const paymentMode = quote.payment_mode ?? quote.type ?? null;
  const paymentProvider = quote.payment_provider ?? quote.latest_payment_provider ?? null;

  if (
    quoteId &&
    paymentMode === "internal" &&
    paymentUrl &&
    (paymentProvider === "stripe" || isStripeCheckoutUrl(paymentUrl))
  ) {
    return `/checkout?quote=${quoteId}`;
  }

  return paymentUrl;
}

export function isStripeCheckoutUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname === "checkout.stripe.com" || parsed.hostname === "checkout.stripe.test";
  } catch {
    return false;
  }
}

export function getQuotePaymentModeLabel(
  mode: QuotePaymentMode | string | null | undefined,
): string {
  return mode === "internal" ? "Shopexperts payment" : "External payment";
}

export function isInternalQuotePayment(
  mode: QuotePaymentMode | string | null | undefined,
): boolean {
  return mode === "internal";
}

export function canExpertManageQuotePaymentStatus(quote?: {
  payment_mode?: QuotePaymentMode | string | null;
}): boolean {
  return !isInternalQuotePayment(quote?.payment_mode);
}

export function canExpertCancelQuote(quote: {
  status: QuoteStatus | string;
  payment_status?: QuotePaymentStatus | string | null;
}): boolean {
  if (quote.status !== "offered") return false;

  const paymentStatus = quote.payment_status ?? "unpaid";
  return paymentStatus === "unpaid" || paymentStatus === "failed";
}

export function getQuoteTotalCents(quote: {
  amount_cents: number;
  quantity: number;
}): number {
  return quote.amount_cents * quote.quantity;
}

export function formatQuoteTotal(quote: {
  amount_cents: number;
  quantity: number;
  currency?: string | null;
}): string {
  return formatCurrency(getQuoteTotalCents(quote), quote.currency);
}

export function getQuoteBudgetLabel(quote: {
  amount_cents: number;
  quantity: number;
  currency?: string | null;
  interval: QuoteInterval | string;
}): string {
  return quote.interval === "hours_bank"
    ? `${formatQuoteTotal(quote)} ${quoteCopy.budgetSuffixes.hours_bank}`
    : formatQuoteTotal(quote);
}

export function formatQuoteIntervalLabel(interval: QuoteInterval | string): string {
  return quoteIntervalLabel(interval);
}

export function formatQuoteStatusLabel(status: QuoteStatus | string): string {
  switch (status) {
    case "offered":
      return "Offered";
    case "accepted":
      return "Accepted";
    case "rejected":
      return "Rejected";
    case "cancelled":
      return "Cancelled";
    default:
      return status.replace(/_/g, " ");
  }
}

export function formatQuotePaymentStatusLabel(
  status: QuotePaymentStatus | string | null | undefined,
): string {
  switch (status ?? "unpaid") {
    case "unpaid":
      return "Unpaid";
    case "partial_paid":
      return "Partially Paid";
    case "paid":
      return "Paid";
    case "partial_refund":
      return "Partial Refund";
    case "refund":
      return "Refund";
    case "failed":
      return "Failed";
    default:
      return String(status).replace(/_/g, " ");
  }
}

export function getQuoteStatusBadgeClass(status: QuoteStatus | string): string {
  switch (status) {
    case "offered":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "accepted":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "rejected":
      return "bg-red-50 text-red-700 border-red-200";
    case "cancelled":
      return "bg-gray-100 text-gray-600 border-gray-200";
    default:
      return "bg-gray-100 text-gray-600 border-gray-200";
  }
}

export function getQuotePaymentBadgeClass(
  status: QuotePaymentStatus | string | null | undefined,
): string {
  switch (status ?? "unpaid") {
    case "paid":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "partial_paid":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "partial_refund":
      return "bg-orange-50 text-orange-700 border-orange-200";
    case "refund":
      return "bg-violet-50 text-violet-700 border-violet-200";
    case "failed":
      return "bg-red-50 text-red-700 border-red-200";
    default:
      return "bg-gray-100 text-gray-500 border-gray-200";
  }
}

export function getQuotePaymentIndicatorClass(
  status: QuotePaymentStatus | string | null | undefined,
): string {
  switch (status ?? "unpaid") {
    case "paid":
      return "bg-emerald-500";
    case "partial_paid":
      return "bg-amber-500";
    case "partial_refund":
      return "bg-orange-500";
    case "refund":
      return "bg-violet-500";
    case "failed":
      return "bg-red-500";
    default:
      return "bg-gray-400";
  }
}
